import React, { useCallback, useEffect, useRef, useState } from 'react';
import Card from '@albertsons/uds/molecule/Card';
import DateSelector from '@albertsons/uds/molecule/DateSelector';
import Button from '@albertsons/uds/molecule/Button';
import './StoreOrderTab.css';
import Papa from 'papaparse';

import { authProvider } from '../../../utils/authProvider';
import { useUserPermissions } from '../../../context/UserPermissionsContext';
import getMsgCount from '../../../services/storeOrderServices/GetMsgCount';
import submitStoreOrder from '../../../services/storeOrderServices/SubmitStoreOrder';
import getDcNames from '../../../services/storeOrderServices/GetDcNames';
import { StoreOrderDTO } from '../dto/StoreOrderDTO';
import AlertNotification from '../../../utils/AlertNotification';

function StoreOrderTab() {
  const envValues = ['PROD'];
  const auth = authProvider();
  const { userPermissions } = useUserPermissions();

  // Get environment from REACT_APP_ENV
  const currentEnv = process.env.REACT_APP_ENV;

  // Environment and DB role-based permissions
  const isProd = currentEnv?.toUpperCase() === 'PROD';
  const isQaOrDev = currentEnv?.toUpperCase() === 'QA' || currentEnv?.toUpperCase() === 'DEV';
  const hasAdminLoadUsers = userPermissions?.roles.some(role =>
    (role.capabilities || []).some(cap => cap.toLowerCase().startsWith('prodorderreplay'))
  ) || false;
  const hasSuperUsersLoad = userPermissions?.roles.some(role =>
    (role.capabilities || []).some(cap => cap.toLowerCase().startsWith('storeorderload'))
  ) || false;
  // Updated permission logic
  const isProdSuperUser = (isProd && hasAdminLoadUsers) || (isQaOrDev && hasSuperUsersLoad);

  // Function to get filtered topic pairs based on environment and permissions
  const getFilteredTopicPairs = (): Record<string, string> => {
    const basePairs: Record<string, string> = {
      'VPT-U': 'DXINAZ_C01_StoreOrder_v1_UAT2',
      'VPT-V': 'DXINAZ_C01_StoreOrder_VPT_V_UAT2',
      'VPT-P': 'DXINAZ_C01_StoreOrder_VPT_P_UAT2',
      'STG-C': 'DXINAZ_C01_StoreOrder_STG_C_UAT2',
       'PREPROD': 'DXINAZ_C01_StoreOrder_PP'
    };

    if (isProd) {
      // PROD environment
       console.log("isProd--------> {} :",isProd);
      if (hasAdminLoadUsers) {
        console.log('User has admin load permissions - showing PRODUCTION-P topic');
        // PROD + Admin Load Users: Only PRODUCTION-P
        return {
          'PRODUCTION-P': 'DXINAZ_StoreOrder'
        };
      } else {
        // PROD + Others: No topics (blocked)
        return {};
      }
    } else {
      if (hasSuperUsersLoad) {
          console.log('User has super user load permissions - showing all topics including PRODUCTION-P');
          
        return {
          ...basePairs,
          'PRODUCTION-P': 'DXINAZ_StoreOrder'
        };
      } else {
        // QA/DEV + Others: Base topics only (no PRODUCTION-P)
        return basePairs;
      }
    }
  };

  const maTargetTopicPairs = getFilteredTopicPairs();
  const availableTopics = Object.keys(maTargetTopicPairs);
  const hasAvailableTopics = availableTopics.length > 0;
  const defaultTarget = hasAvailableTopics ? availableTopics[0] : '';
  
  const [form, setForm] = useState<StoreOrderDTO>({
    selectedEnv: 'PROD',
    divisionId: '',
    warehouseId: '',
    retailStoreId: '',
    storeOrderNbr: '',
    fromDate: undefined,
    toDate: undefined,
    sourceProcessDate: undefined,
    scheduledDeliveryDate: undefined,
    targetProcessDate: undefined,
    targetDeliveryDate: undefined,
    selectedDC: '',
    maTarget: defaultTarget,
    targetKafkaTopic: hasAvailableTopics ? maTargetTopicPairs[defaultTarget] : '',
    chgTicketNbr: '',
    userId: auth.userId,
    buttonType: '',
    activeUserName: auth.userId,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof StoreOrderDTO, string>>>({});
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [, setResponse] = useState<any>(null);
  const [, setIsLoading] = useState(false);
  const [isOpen, setOpen] = useState(true);
  const [showPublishPopup, setShowPublishPopup] = useState(false);
  const [, setPublishConfirmed] = useState(false);
  const [dcNames, setDcNames] = useState<string[]>([]); // Empty array as default

  // Refs
  const notificationRef = useRef<HTMLDivElement | null>(null);

  // Fetch DC names on component mount
  useEffect(() => {
    const fetchDcNames = async () => {
      try {
        const result = await getDcNames();
        if (Array.isArray(result)) {
          setDcNames(result);
          // Update form selectedDC if current selection is not in the new list
          setForm(prev => {
            if (!result.includes(prev.selectedDC)) {
              return { ...prev, selectedDC: result[0] || '' };
            }
            return prev;
          });
        } else {
          console.error('Failed to fetch DC names:', result.error);
          // Keep empty array on error - no hardcoded defaults
        }
      } catch (error) {
        console.error('Error fetching DC names:', error);
      }
    };

    fetchDcNames();
  }, []);

  useEffect(() => {
  const newTopic = maTargetTopicPairs[form.maTarget] || '';
  if (form.targetKafkaTopic !== newTopic) {
    setForm(prev => ({
      ...prev,
      targetKafkaTopic: newTopic,
    }));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [form.maTarget, maTargetTopicPairs]);

  useEffect(() => {
    if (notification && notificationRef.current) {
      setTimeout(() => {
        notificationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [notification]);

  // Helpers
  const handleFormChange = useCallback(
    <K extends keyof StoreOrderDTO>(key: K, value: StoreOrderDTO[K]) => {
      setForm(prev => ({ ...prev, [key]: value }));
      setErrors(prev => ({ ...prev, [key]: undefined }));
    },
    []
  );

  const validateGetMsg = (): boolean => {
    const newErrors: Partial<Record<keyof StoreOrderDTO, string>> = {};
    const requiredFields: (keyof StoreOrderDTO)[] = [
      'selectedEnv',
      'fromDate',
      'toDate',
      'sourceProcessDate',
      'selectedDC'
    ];

    requiredFields.forEach(field => {
      if (!form[field]) newErrors[field] = `${field} is required`;
    });

    if (form.fromDate && form.toDate && form.toDate < form.fromDate)
      newErrors.toDate = 'To Date should be ≥ From Date';

    if (form.sourceProcessDate && form.scheduledDeliveryDate && form.scheduledDeliveryDate < form.sourceProcessDate)
      newErrors.scheduledDeliveryDate = 'Source Scheduled Delivery Date should be ≥ Source Process Date';

    form.buttonType = 'COUNT';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePublish = (): boolean => {
    const newErrors: Partial<Record<keyof StoreOrderDTO, string>> = {};
    const requiredFields: (keyof StoreOrderDTO)[] = [
      'selectedEnv',
      'selectedDC',
      'fromDate',
      'toDate',
      'sourceProcessDate',
      'targetProcessDate',
      'targetDeliveryDate',
      'maTarget',
      'targetKafkaTopic',
    ];

    // Check if user has access to any topics
    if (!hasAvailableTopics) {
      newErrors.maTarget = 'You do not have permission to publish to any topics';
    }

    if (isProdSuperUser) requiredFields.push('chgTicketNbr');

    requiredFields.forEach(field => {
      if (!form[field]) newErrors[field] = `${field} is required`;
    });

    if (form.fromDate && form.toDate && form.toDate < form.fromDate)
      newErrors.toDate = 'To Date should be ≥ From Date';

    if (form.sourceProcessDate && form.scheduledDeliveryDate && form.scheduledDeliveryDate < form.sourceProcessDate)
      newErrors.scheduledDeliveryDate = 'Source Scheduled Delivery Date should be ≥ Source Process Date';

    if (form.targetProcessDate && form.targetDeliveryDate && form.targetDeliveryDate < form.targetProcessDate)
      newErrors.targetDeliveryDate = 'Target Delivery Date should be ≥ Target Process Date';

    form.buttonType = 'PUBLISH';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers
  const handleClear = () => {
    setForm({
      selectedEnv: 'PROD',
      divisionId: '',
      warehouseId: '',
      retailStoreId: '',
      storeOrderNbr: '',
      fromDate: undefined,
      toDate: undefined,
      sourceProcessDate: undefined,
      scheduledDeliveryDate: undefined,
      targetProcessDate: undefined,
      targetDeliveryDate: undefined,
      selectedDC: '',
      maTarget: defaultTarget,
      targetKafkaTopic: hasAvailableTopics ? maTargetTopicPairs[defaultTarget] : '',
      chgTicketNbr: '',
      userId: auth.userId,
      buttonType: '',
      activeUserName: auth.userId,
      
    });
    setErrors({});
  };

  const handlePublishClick = () => {
    if (!validatePublish()) return;
    setShowPublishPopup(true);
  };

const handlePopupOk = async () => {
  setShowPublishPopup(false);
  setIsLoading(true);

  try {
    const response = await submitStoreOrder(form, setIsLoading);
    setResponse(response); // Optional: update global state

    const { infoMsg, errorMsg } = response;

    if (errorMsg) {
      setNotification({
        type: 'error',
        message: errorMsg.replace(/\n/g, ' ')
      });
    } else if (infoMsg) {
      setNotification({
        type: 'success',
        message: infoMsg.replace(/\n/g, ' ')
      });
    }

    setPublishConfirmed(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });

  } catch {
    setNotification({
      type: 'error',
      message: 'Unexpected error occurred during submission.'
    });
    setIsLoading(false);
  }
};


  const handlePopupCancel = () => {
    setShowPublishPopup(false);
    setPublishConfirmed(false);
  };

  const handleMessageCount = async () => {
    if (!validateGetMsg()) return;
    await getMsgCount(form, setIsLoading, (response) => {
      setResponse(response);

      if ('error' in response) {
        setNotification({ type: 'error', message: response.error });
      } else if (response.errorMsg) {
        setNotification({ type: 'error', message: response.errorMsg });
      } else if (response.infoMsg) {
        setNotification({ type: 'success', message: response.infoMsg });
      } else {
        setNotification({ type: 'success', message: 'Message count retrieved successfully.' });
      }
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [, setFirstRow] = useState<string[] | null>(null);

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (fileExtension === 'txt') {
        // Handle TXT files - read as plain text and split by lines/commas
        const reader = new FileReader();
        reader.onload = (event) => {
          const text = event.target?.result as string;
          const allValues = text
            .split(/\r?\n/)
            .map(line => line.split(','))
            .flat()
            .map(cell => String(cell).trim())
            .filter(Boolean);
          const csvValues = allValues.join(',');
          
          form.storeOrderNbr = form.storeOrderNbr
            ? `${form.storeOrderNbr},${csvValues}`
            : csvValues;
          
          setFirstRow(allValues);
        };
        reader.readAsText(file);
      } else {
        // Handle CSV files with Papa.parse
        Papa.parse(file, {
          complete: (results) => {
            const allRows = results.data as string[][];

            // Flatten and stringify all non-empty cells
            const allValues = allRows
              .flat()
              .map((cell) => String(cell).trim())
              .filter(Boolean); // remove empty strings

            const csvValues = allValues.join(',');

            form.storeOrderNbr = form.storeOrderNbr
              ? `${form.storeOrderNbr},${csvValues}`
              : csvValues;

            setFirstRow(allValues); // optional: update state with all values
          },
          error: (err) => {
            console.error('CSV parsing error:', err);
          },
        });
      }
    }
  };



  const handleFileRemove = () => {
    setSelectedFile(null);
    setFirstRow(null);
    const input = document.getElementById('csvUpload') as HTMLInputElement;
    if (input) input.value = '';
  };


  return (
    <div className="store-order-tab-wrapper">
     {notification && (
      <AlertNotification
        type={notification.type}
        message={notification.message.replace(/\n/g, ' ')}
        onClose={() => setNotification(null)}
      />
    )}

      <Card
        collapsible
        title="Source Query Parameters"
        isOpen={isOpen}
        onChange={setOpen}
        className="min-w-[600px] mt15"
      >
        <Card.Content>
          <div className="flex-container">
            <div>Source</div>
            <div>DC</div>
            <div>Division</div>
          </div>
          <div className="flex-container">
            <div className="inner-box">
              <select
                className="dropdown"
                value={form.selectedEnv}
                onChange={(e) => handleFormChange('selectedEnv', e.target.value)}
              >
                {envValues.map((env) => (
                  <option key={env} value={env}>
                    {env}
                  </option>
                ))}
              </select>
              {errors.selectedEnv && <div className="error-message">{errors.selectedEnv}</div>}
            </div>
            <div className="inner-box">
              <select
                className="dropdown"
                value={form.selectedDC}
                onChange={(e) => handleFormChange('selectedDC', e.target.value)}
              >
                {dcNames.map((dc) => (
                  <option key={dc} value={dc}>
                    {dc}
                  </option>
                ))}
              </select>
              {errors.selectedDC && <div className="error-message">{errors.selectedDC}</div>}
            </div>
            <div className="inner-box">
              <input
                type="text"
                className="input-fields-so text-light-text"
                value={form.divisionId}
                onChange={(e) => handleFormChange('divisionId', e.target.value)}
              />
              {errors.divisionId && <div className="error-message">{errors.divisionId}</div>}
            </div>
          </div>
          <div className="flex-container">
            <div>Warehouse ID</div>
            <div>Retail Store ID</div>
          </div>
          <div className="flex-container">
            <div>
              <input
                type="text"
                className="input-fields-so text-light-text"
                placeholder="Multiple Warehouse IDs separated by comma"
                value={form.warehouseId}
                onChange={(e) => handleFormChange('warehouseId', e.target.value)}
              />
              {errors.warehouseId && <div className="error-message">{errors.warehouseId}</div>}
            </div>
            <div>
              <input
                type="text"
                className="input-fields-so text-light-text"
                placeholder="Multiple Retail Store IDs separated by comma"
                value={form.retailStoreId}
                onChange={(e) => handleFormChange('retailStoreId', e.target.value)}
              />
              {errors.retailStoreId && <div className="error-message">{errors.retailStoreId}</div>}
            </div>
          </div>
          <div className="flex-container">
            <div>Store Order No</div>
          </div>
          <div className="flex-container">
            <div>
              <input
                type="text"
                className="input-fields-so store-order-nbr text-light-text"
                placeholder="Multiple Store Order Numbers separated by comma"
                value={form.storeOrderNbr}
                onChange={(e) => handleFormChange('storeOrderNbr', e.target.value)}
              />
              {errors.storeOrderNbr && <div className="error-message">{errors.storeOrderNbr}</div>}
            </div>
            <div className="upload-wrapper">
              <input
                type="file"
                id="csvUpload"
                accept=".csv,.txt"
                style={{ display: 'none' }}
                onChange={handleCsvUpload}
              />
              {!selectedFile && (
                <Button
                  className="button-white-bg margin-top-29px"
                  reset
                  onClick={() => document.getElementById('csvUpload')?.click()}
                >
                  Upload File
                </Button>
              )}
              {selectedFile && (
                <div className="file-input-wrapper">
                  <input
                    type="text"
                    className="input-fields-so store-order-nbr text-light-text"
                    value={selectedFile.name}
                    readOnly
                  />
                  <span className="delete-icon" onClick={handleFileRemove}>✖</span>
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="flex-container">
            <div>Create Date Range</div>
          </div>
          <div className="flex-container mt-1rem">
            <div>
              <DateSelector
                value={form.fromDate}
                onChange={(date) => handleFormChange('fromDate', date)}
              />
              {errors.fromDate && <div className="error-message">{errors.fromDate}</div>}
            </div>
            <div>
              <DateSelector
                value={form.toDate}
                onChange={(date) => handleFormChange('toDate', date)}
              />
              {errors.toDate && <div className="error-message">{errors.toDate}</div>}
            </div>
          </div>

          <div className="flex-container">
            <div>Source Process Date</div>
            <div>Source Scheduled Delivery Date</div>
          </div>
          <div className="flex-container mt-1rem">
            <div>
              <DateSelector
                value={form.sourceProcessDate}
                onChange={(date) => handleFormChange('sourceProcessDate', date)}
              />
              {errors.sourceProcessDate && <div className="error-message">{errors.sourceProcessDate}</div>}
            </div>
            <div>
              <DateSelector
                value={form.scheduledDeliveryDate}
                onChange={(date) => handleFormChange('scheduledDeliveryDate', date)}
              />
              {errors.scheduledDeliveryDate && <div className="error-message">{errors.scheduledDeliveryDate}</div>}
            </div>
          </div>
        </Card.Content>
      </Card>

      <Card
        collapsible
        title="Target Parameters"
        isOpen={isOpen}
        onChange={setOpen}
        className="min-w-[600px] mt15"
      >
        <Card.Content>
          <div className="flex-container">
            <div>Target Process Date</div>
            <div>Target Delivery Date</div>
          </div>
          <div className="flex-container mt-1rem">
            <div>
              <DateSelector
                value={form.targetProcessDate}
                onChange={(date) => handleFormChange('targetProcessDate', date)}
              />
              {errors.targetProcessDate && <div className="error-message">{errors.targetProcessDate}</div>}
            </div>
            <div>
              <DateSelector
                value={form.targetDeliveryDate}
                onChange={(date) => handleFormChange('targetDeliveryDate', date)}
              />
              {errors.targetDeliveryDate && <div className="error-message">{errors.targetDeliveryDate}</div>}
            </div>
          </div>

          <div className="flex-container">
            <div>MA Target</div>
            <div>Target Kafka Topic</div>
            {form.maTarget==='PRODUCTION-P' && <div>Change Ticket Number</div>}
          </div>
          <div className="flex-container">
            <div className="inner-box">
              {hasAvailableTopics ? (
                <select
                  className="dropdown input-fields mafield text-light-text"
                  value={form.maTarget}
                  onChange={(e) => handleFormChange('maTarget', e.target.value)}
                >
                  {availableTopics.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="input-fields-so mafield text-light-text no-access-message">
                  No topics available - insufficient permissions
                </div>
              )}
              {errors.maTarget && <div className="error-message">{errors.maTarget}</div>}
            </div>
            <div className="inner-box">
              <input
                type="text"
                className="input-fields-so mafield text-light-text"
                placeholder="Value will appear here"
                value={form.targetKafkaTopic}
                onChange={(e) => handleFormChange('targetKafkaTopic', e.target.value)}
              />
              {errors.targetKafkaTopic && <div className="error-message">{errors.targetKafkaTopic}</div>}
            </div>
            {form.maTarget==='PRODUCTION-P' && (
              <div className="inner-box">
                <input
                  type="text"
                  className="input-fields-so mafield text-light-text"
                  value={form.chgTicketNbr}
                  onChange={(e) => handleFormChange('chgTicketNbr', e.target.value)}
                />
                {errors.chgTicketNbr && <div className="error-message">{errors.chgTicketNbr}</div>}
              </div>
            )}
          </div>

          <div className="flex-container-button right-align">
            <div>
              <Button 
                className="button-white-bg" 
                reset 
                onClick={handleMessageCount}
                disabled={!hasAvailableTopics}
              >
                Message Count
              </Button>
              <Button 
                className="button-white-bg" 
                reset 
                onClick={handlePublishClick}
                disabled={!hasAvailableTopics}
              >
                Publish Messages
              </Button>
              <Button className="button-clear-bg" onClick={handleClear}>
                Clear
              </Button>
            </div>
          </div>
        </Card.Content>
      </Card>

      {showPublishPopup && (
        <div className="custom-popup-overlay">
          <div className="custom-popup">
            <h2 className="popup-heading">Confirm Upload</h2>
            <p>Do you want to upload Store Order to <b>{form.maTarget}</b>?</p>


            <div className="popup-actions">
              <button className="ok-button" onClick={handlePopupOk}>OK</button>
              <button className="cancel-button" onClick={handlePopupCancel}>
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
export default StoreOrderTab;
